// .env* で設定された環境変数をここで集中管理する
import { ROLE, MENU, FULLSCREEN_MODE } from '../lib/constants';
import ColorSF2 from '../styles/common/ColorSF2';

const version = process.env.REACT_APP_VERSION;
const baseURL = process.env.REACT_APP_API_URL;
const useDevTool = process.env.REACT_APP_USE_REDUX_DEV_TOOL === 'yes';
const qrcodeUpdatableDays = process.env.REACT_APP_QRCODE_UPDATABLE_DAYS;
const project = {
  title: process.env.REACT_APP_PROJECT_TITLE,
  name: process.env.REACT_APP_PROJECT_NAME,
};
const locale = process.env.REACT_APP_LOCALE;
const googleMapApiKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
const mapDefaultLat = process.env.REACT_APP_MAP_DEFAULT_LAT;
const mapDefaultLng = process.env.REACT_APP_MAP_DEFAULT_LNG;
const roomServerWebSockURL = `${process.env.REACT_APP_ROOM_SERVER}/api/room/websocket`;
const iOsSupportVersion = process.env.REACT_APP_IOS_SUPPORT_VERSION;
const androidSupportVersion = process.env.REACT_APP_ANDROID_SUPPORT_VERSION;
const compatibleLowerLimitHeight =
  process.env.REACT_APP_COMPATIBLE_LOWER_LIMIT_HEIGHT;
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const appEnvironment = process.env.REACT_APP_ENVIRONMENT;

// datadog関連設定
const DATADOG_CONFIG = {
  dev: {
    applicationId: '1c1afc68-95f7-40b1-8e16-811394e27fa2',
    clientToken: 'pub458626723fa5ed6c994168f88184e2d3',
  },
  stg: {
    applicationId: 'dcbfeb61-c2a4-4f1b-bfee-4ea3a68e4b72',
    clientToken: 'pub44cffdf3e41c868566ae87d973331bb2',
  },
  prod: {
    applicationId: '9dd875a6-b570-4c01-a198-43f619a0afa4',
    clientToken: 'pub14b501a6f5bc10a7061a542075460fd5',
  },
};
const datadog = {
  applicationId:
    (appEnvironment === 'production' && DATADOG_CONFIG.prod.applicationId) ||
    (appEnvironment === 'staging' && DATADOG_CONFIG.stg.applicationId) ||
    DATADOG_CONFIG.dev.applicationId,
  clientToken:
    (appEnvironment === 'production' && DATADOG_CONFIG.prod.clientToken) ||
    (appEnvironment === 'staging' && DATADOG_CONFIG.stg.clientToken) ||
    DATADOG_CONFIG.dev.clientToken,
  site: 'datadoghq.com',
  service: 'smartfield_web',
  env: appEnvironment,
  version,
  defaultPrivacyLevel: 'mask-user-input',
};

// 設定メニュー
const SETTING_MENUS = [
  MENU.staff,
  MENU.company,
  MENU.device,
  MENU.master,
  MENU.group,
  MENU.log,
  MENU.actionLog,
];
const MENU_ACCESS = {
  [ROLE.GUEST]: [MENU.dashBoard, MENU.issue, MENU.report, MENU.actionLog],
  [ROLE.GENERAL]: [
    MENU.dashBoard,
    MENU.issue,
    MENU.media,
    MENU.report,
    MENU.actionLog,
  ],
  [ROLE.ADMIN]: [
    MENU.dashBoard,
    MENU.issue,
    MENU.staff,
    MENU.device,
    MENU.master,
    MENU.group,
    MENU.actionLog,
    MENU.report,
    MENU.media,
    MENU.accessControl,
  ],
  [ROLE.COMPANY_ADMIN]: [
    MENU.dashBoard,
    MENU.issue,
    MENU.staff,
    MENU.company,
    MENU.device,
    MENU.master,
    MENU.group,
    MENU.actionLog,
    MENU.report,
    MENU.media,
    MENU.accessControl,
  ],
  [ROLE.SERVICE_VENDOR]: [MENU.staff, MENU.company],
  [ROLE.ROOT]: [MENU.company],
};
// グループ選択ボックスを表示する画面一覧
const GROUP_SELECT_BOX_DISPLAY_MODES = [
  FULLSCREEN_MODE.dashboard,
  FULLSCREEN_MODE.timeline,
  FULLSCREEN_MODE.timelineTile,
  FULLSCREEN_MODE.report,
];

const customRole = {
  // ロールによって許可される権限変更先(現状は作業者管理専用)
  updateRole: {
    [ROLE.GENERAL]: [],
    [ROLE.ADMIN]: [],
    [ROLE.COMPANY_ADMIN]: [
      ROLE.GUEST,
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
    ],
    [ROLE.SERVICE_VENDOR]: [
      ROLE.GUEST,
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
    ],
  },
  // ロールによって参照可能な権限(現状は作業者管理専用)
  // ユーザ権限管理で自分より上位の権限を持つユーザを表示しないための検索オプションとして使う
  searchRole: {
    [ROLE.ADMIN]: [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN],
    [ROLE.COMPANY_ADMIN]: [
      ROLE.GUEST,
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
    ],
    [ROLE.SERVICE_VENDOR]: [
      ROLE.GUEST,
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
      ROLE.SERVICE_VENDOR,
    ],
  },
  // ページの表示が許可されるロール一覧
  pageAccess: {
    dashBoard: [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN], // ダッシュボード
    media: [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN], // メディアページ
    timeline: [ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN], // タイムライン
    timelineTile: [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN], // タイムラインタイルページ
    staff: [ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // ユーザ情報変更
    company: [ROLE.SERVICE_VENDOR, ROLE.COMPANY_ADMIN, ROLE.ROOT], // 企業情報変更
    device: [ROLE.ADMIN, ROLE.COMPANY_ADMIN], // デバイス情報変更
    master: [ROLE.ADMIN, ROLE.COMPANY_ADMIN], // タグ情報
    remote: [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN], // 遠隔作業支援
    activation: [ROLE.INITIAL], // アクティベート
    group: [ROLE.ADMIN, ROLE.COMPANY_ADMIN], // グループ管理
    report: [ROLE.GUEST, ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.GENERAL], // 報告書一覧
    actionLog: [ROLE.GUEST, ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.GENERAL], // アクティビティログ
    accessControl: [ROLE.ADMIN, ROLE.COMPANY_ADMIN], // アクセス制御
  },
  // 機能の実行権限
  functionAccess: {
    createCompany: [ROLE.SERVICE_VENDOR, ROLE.ROOT], // 企業作成
    searchCompany: [ROLE.SERVICE_VENDOR, ROLE.ROOT], // 企業検索
    deleteCompany: [ROLE.SERVICE_VENDOR, ROLE.ROOT], // 企業削除
    updateCompany: [ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // 企業編集
    searchCompanyLocation: [ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // 住所検索（企業管理）
    deleteDataAll: [ROLE.SERVICE_VENDOR, ROLE.ROOT], // 企業データ全削除
    deleteData: [
      ROLE.GUEST,
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
      ROLE.ROOT,
    ], // データ個別削除
    restoreDeleteData: [
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
      ROLE.ROOT,
    ], // データ復元
    completeDeleteData: [
      ROLE.GENERAL,
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
      ROLE.ROOT,
    ], // 完全データ削除
    searchUserName: [ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // 作業者名検索
    searchUserRole: [ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // 権限検索
    searchAccountStatus: [ROLE.ADMIN, ROLE.COMPANY_ADMIN, ROLE.SERVICE_VENDOR], // ライセンス(状態)検索
    searchRemoteSupportStatus: [
      ROLE.ADMIN,
      ROLE.COMPANY_ADMIN,
      ROLE.SERVICE_VENDOR,
    ], // 遠隔支援状態検索
    searchDevice: [ROLE.ADMIN, ROLE.COMPANY_ADMIN], // デバイス検索
    updateDeviceNickname: [ROLE.COMPANY_ADMIN], // デバイス更新
    updateLicense: [ROLE.SERVICE_VENDOR], // ライセンス管理
    displayLicense: [ROLE.COMPANY_ADMIN], // ライセンス表示&自動解析管理
  },
};
const ACCOUNT_STATUS = {
  ENABLE: 1,
  DISABLE: 2,
  DELETE: 0,
  ALL: 9,
};
const searchAccountStatus = {
  enable: [ACCOUNT_STATUS.ENABLE],
  notDeleted: [ACCOUNT_STATUS.ENABLE, ACCOUNT_STATUS.DISABLE],
  all: [ACCOUNT_STATUS.ENABLE, ACCOUNT_STATUS.DISABLE, ACCOUNT_STATUS.DELETE],
};
// LocalStorageキー管理
const localStorageKey = {
  timeline: {
    // 条件検索
    recordedDateFrom: 'timeline_recorded_date_from',
    recordedDateTo: 'timeline_recorded_date_to',
    partialOperatorName: 'timeline_partial_operator_name',
    partialDeviceNickname: 'timeline_partial_device_nickname',
    freeTag: 'timeline_free_tag',
    tags: 'timeline_tags',
    selectedTagIds: 'timeline_selected_tag_ids',
    isSearchForUnsetTagOnly: 'timeline_is_search_for_unset_tag_only',
    selectedGroupIds: 'timeline_selected_group_ids',
  },
  remote: {
    // 黒板UI設定
    color: 'remote_blackboard_color',
    opacity: 'remote_blackboard_opacity',
    position: 'remote_blackboard_position',
  },
  dashboard: {
    targetCompanyGuid: 'dashboard_target_company_guid', // 表示対象企業GUID
    targetGroupId: 'dashboard_target_group_id', // 表示対象グループGUID
    latestGroupIds: 'dashboard_latest_group_ids', // 直近設定グループIDリスト
  },
};

const sessionStorageKey = {
  remote: {
    oppositeOperatorIds: 'remote_opposite_operator_ids',
    blackboardId: 'remote_blackboard_id',
    oppositeDeviceId: 'remote_opposite_device_id',
    appRoomId: 'remote_app_room_id',
    favoriteId: 'remote_favorite_id',
  },
};

// 黒板設定用パラメータ
const blackboardParam = {
  colors: {
    green: ColorSF2.green,
    red: ColorSF2.alertRed,
    blue: ColorSF2.brightBlue,
    black: ColorSF2.black,
  },
  positions: {
    left_top: 0x00,
    right_top: 0x20,
    left: 0x01,
    right: 0x21,
    left_bottom: 0x02,
    right_bottom: 0x22,
  },
};

// かん助 URL
const cansuke = 'https://cansuke.jp';

// 表示タグ設定
const DISPLAY_TAG = {
  use: 0,
  delete: 1,
  all: 2,
};

// Google Maps API
const googleMapsApi = (longitude, latitude) =>
  `https://www.google.com/maps/search/?api=1&query=${longitude}%2C${latitude}`;

export default {
  version,
  baseURL,
  googleMapApiKey,
  mapDefaultLat,
  mapDefaultLng,
  useDevTool,
  qrcodeUpdatableDays,
  project,
  locale,
  datadog,
  customRole,
  accountStatus: ACCOUNT_STATUS,
  searchAccountStatus,
  roomServerWebSockURL,
  iOsSupportVersion,
  androidSupportVersion,
  compatibleLowerLimitHeight,
  sentryDsn,
  appEnvironment,
  localStorageKey,
  blackboardParam,
  cansuke,
  SETTING_MENUS,
  MENU_ACCESS,
  sessionStorageKey,
  DISPLAY_TAG,
  googleMapsApi,
  GROUP_SELECT_BOX_DISPLAY_MODES,
};
